import ThumbImg from "../media/ThumbImg";
import { useMemo, useRef, useState } from 'react';
import { spaceUnreadTotals, anyDmUnread } from '../../lib/unread';
import { mergeOrder } from './slotReorder';
import { useSlotReorder } from './useSlotReorder';

// Structural subset of ChatLayout's VoidWS — only what the rail renders.
type RailSpace = { id: string; name: string; avatarUrl?: string | null };

type SpaceRailProps = {
  voids: RailSpace[];
  hiddenSpaces: Record<string, boolean>;
  spaceOrder: string[];
  setSpaceOrder: React.Dispatch<React.SetStateAction<string[]>>;
  unread: Record<string, number>;
  currentVoidId: string;
  dmsActive: boolean;
  me: { userId?: string; name?: string; avatarUrl?: string | null };
  fallbackInitial?: string;
  meStatus: string;
  userQuickOpen: boolean;
  friendReqCount: number;
  unreadNotificationCount: number;
  onHome: () => void;
  onOpenDms: () => void;
  onSwitchVoid: (id: string) => void;
  onCreateSpace: () => void;
  onToggleUserQuick: () => void;
  onOpenNotifications: () => void;
};

function SpaceRail({
  voids,
  hiddenSpaces,
  spaceOrder,
  setSpaceOrder,
  unread,
  currentVoidId,
  dmsActive,
  me,
  fallbackInitial,
  meStatus,
  userQuickOpen,
  friendReqCount,
  unreadNotificationCount,
  onHome,
  onOpenDms,
  onSwitchVoid,
  onCreateSpace,
  onToggleUserQuick,
  onOpenNotifications,
}: SpaceRailProps) {
  const [reorderedSpaceId, setReorderedSpaceId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const hasDmUnread = anyDmUnread(voids, unread);
  const spaceTotals = useMemo(() => spaceUnreadTotals(unread), [unread]);

  const nonDm = voids.filter(v => !String(v.id).startsWith('dm_') && !hiddenSpaces[v.id]);
  const ordered = [...nonDm].sort((a, b) => {
    const ia = spaceOrder.indexOf(a.id); const ib = spaceOrder.indexOf(b.id);
    return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
  });

  // Drag-to-reorder: the dragged icon follows the pointer on Y only and its neighbours slide aside (see useSlotReorder).
  const reorder = useSlotReorder({
    axis: 'y',
    ids: ordered.map((s) => s.id),
    scrollRef: listRef,
    onReorder: (ids) => setSpaceOrder((prev) => mergeOrder(prev, ids)),
  });

  const renderSpaceButton = (v: RailSpace, idx: number) => {
    const spaceUnread = spaceTotals[v.id] || 0;
    const dragging = reorder.dragId === v.id;
    return (
      <div key={v.id} ref={reorder.itemRef(idx)} className="relative" style={reorder.itemStyle(idx, v.id)}>
        <button
          {...reorder.itemProps(idx, v.id)}
          onClick={() => {
            if (reorder.consumeClick()) return;
            onSwitchVoid(v.id); setReorderedSpaceId(v.id); setTimeout(() => setReorderedSpaceId(null), 260);
          }}
          title={v.name}
          aria-roledescription="reorderable space"
          className={`relative h-11 w-11 rounded-xl overflow-hidden ${v.id === currentVoidId ? 'bg-white/[0.12] text-fg' : 'bg-elevated hover:bg-white/[0.08]'} flex items-center justify-center ${dragging ? 'ring-2 ring-emerald-600 shadow-[0_12px_24px_rgba(0,0,0,0.45)] cursor-grabbing' : 'transition-transform duration-150 ease-out active:scale-95'} ${reorderedSpaceId === v.id ? 'anim-pop' : ''}`}
        >
          {v.avatarUrl ? (
            <ThumbImg w={56} src={v.avatarUrl} alt={v.name} className="h-full w-full object-cover" draggable={false} />
          ) : (
            <span className="text-meta text-fg-2">{(v.name?.[0] || '?').toUpperCase()}</span>
          )}
        </button>
        {v.id === currentVoidId ? <span aria-hidden className="absolute -left-2.5 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-brand" /> : null}
        {spaceUnread > 0 ? <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-neutral-950"></span> : null}
      </div>
    );
  };

  return (
    <nav aria-label="Spaces" className="hidden md:flex w-16 bg-panel-3 flex-col py-3 min-h-0">
      {/* Home button at top */}
      <div className="px-1 flex items-center justify-center">
        <button
          className="h-11 w-11 rounded-xl bg-elevated text-fg-2 hover:bg-white/[0.08] hover:text-fg flex items-center justify-center"
          title="Home"
          aria-label="Home"
          onClick={onHome}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <path d="M3 9l9-7 9 7" /><path d="M9 22V12h6v10" />
          </svg>
        </button>
      </div>
      <div className="mt-3 px-2 flex items-center justify-center">
        {/* The indicator and badge sit beside the button, not inside it: the button clips its contents to the rounded tile. */}
        <div className="relative h-11 w-11">
        <button
          className={`relative h-11 w-11 overflow-hidden rounded-xl transition-colors ${
            dmsActive ? 'bg-white/[0.12] text-fg' : 'bg-elevated hover:bg-white/[0.08]'
          }`}
          title="Open Direct Messages"
          onClick={onOpenDms}
          aria-label="Open Direct Messages"
        >
          <span className="absolute inset-0 flex items-center justify-center text-fg-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
          </span>
        </button>
        {dmsActive ? <span aria-hidden className="absolute -left-2.5 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-brand" /> : null}
        {hasDmUnread ? <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-neutral-950"></span> : null}
        </div>
      </div>
      {/* Scrollable spaces */}
      <div ref={listRef} className="mt-2 flex-1 overflow-auto flex flex-col items-center gap-3 pb-24">
        <div className="w-full px-2 text-meta text-fg-3 text-center">Spaces</div>
        {ordered.length > 0 ? (
          <div className="flex flex-col items-center gap-3">
            {ordered.map(renderSpaceButton)}
          </div>
        ) : (
          <div className="text-meta text-fg-3 text-center px-3">No spaces yet.</div>
        )}
        {/* New Space button pinned to bottom of the scrollable list so it doesn't get covered by the fixed footer */}
        <div className="sticky bottom-2 w-full flex justify-center px-2 pt-1">
          <button
            className="h-10 w-10 rounded-full border border-transparent bg-neutral-900 text-fg-2 hover:border-emerald-600"
            title="New Space"
            onClick={onCreateSpace}
          >
            +
          </button>
        </div>
      </div>
      {me?.userId && (
        <div className="px-1 pt-2">
          {/* The notification badge sits on this relative wrapper as a sibling of
              the button: a <button> nested inside the quick-settings <button> is
              invalid HTML and let a tap on the badge also toggle quick settings. */}
          <div className="relative">
            <button
              className={`relative h-12 w-12 rounded-full border ${userQuickOpen ? 'border-emerald-500 shadow-[0_8px_24px_rgba(16,185,129,0.35)]' : 'border-transparent'} bg-elevated flex items-center justify-center transition duration-200`}
              title="Quick settings"
              aria-label="Quick settings"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onToggleUserQuick(); }}
              onClick={(e) => { e.stopPropagation(); onToggleUserQuick(); }}
            >
              {friendReqCount > 0 && (
                <span className="pointer-events-none absolute -top-1 -right-1 min-w-[16px] px-1 py-0.5 rounded-full bg-emerald-500 text-meta font-semibold text-black ring-2 ring-neutral-900 text-center leading-none">
                  {Math.min(99, friendReqCount)}
                </span>
              )}
              <span className="h-11 w-11 rounded-full overflow-hidden bg-neutral-800 border border-neutral-700 flex items-center justify-center">
                {me?.avatarUrl ? (
                  <ThumbImg w={56} src={me.avatarUrl} alt={me?.name || 'me'} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-meta text-fg-2">{(me?.name?.[0] || fallbackInitial || '?').toUpperCase()}</span>
                )}
              </span>
              {(() => {
                const color = meStatus==='online' ? 'bg-online' : meStatus==='idle' ? 'bg-amber-500' : meStatus==='dnd' ? 'bg-red-500' : 'bg-neutral-500';
                return <span className={`absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-neutral-950 ${color}`}></span>;
              })()}
            </button>
            {unreadNotificationCount > 0 && (
              <button
                className="absolute -top-1 -left-1 min-w-[16px] px-1 py-0.5 rounded-full bg-amber-400 text-meta font-medium text-black ring-2 ring-neutral-900 text-center leading-none"
                onClick={(e) => { e.stopPropagation(); onOpenNotifications(); }}
                title="View notifications"
                aria-label="View notifications"
              >
                {Math.min(99, unreadNotificationCount)}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default SpaceRail;
